import { Controller } from "@hotwired/stimulus"

/**
 * Optimistic emoji reactions (CONEK-8845).
 *
 * data-controller="support--reactions" on Support::ReactionsComponent's bar.
 * Every pill and picker button is a button_to form posting the toggle. On
 * submit the bar applies the toggle at once: the viewer's pill loses or gains
 * one, a new emoji clones the pill template, and the picker closes. The
 * server's turbo-stream then replaces the whole bar with the real state (and
 * the hover list of names); a failed request puts the bar back as it was.
 *
 * Pill state lives in data-active (the Ui::ButtonComponent toggle colours),
 * so no design-system class is named here.
 */
export default class extends Controller {
  static targets = ["pill", "template", "add"]

  toggle(event) {
    const emoji = new FormData(event.target).get("emoji")
    if (!emoji) return

    this.snapshot ??= this.element.innerHTML
    const pill = this.pillTargets.find((el) => el.dataset.reaction === emoji)

    if (!pill) {
      this.#insert(emoji)
    } else if (pill.dataset.active === "true") {
      const count = this.#count(pill) - 1
      if (count === 0) {
        pill.closest("form").remove()
      } else {
        this.#render(pill, emoji, count, false)
      }
    } else {
      this.#render(pill, emoji, this.#count(pill) + 1, true)
    }

    this.#closePicker()
  }

  revert(event) {
    const snapshot = this.snapshot
    this.snapshot = undefined
    if (event.detail.success || snapshot === undefined) return

    this.element.innerHTML = snapshot
  }

  #insert(emoji) {
    const form = this.templateTarget.content.firstElementChild.cloneNode(true)
    form.querySelector("input[name='emoji']").value = emoji
    this.#render(form.querySelector("button"), emoji, 1, true)

    // Keep the picker's order: before the first pill that comes later in it.
    const order = this.#pickerOrder()
    const next = this.pillTargets.find(
      (el) => order.indexOf(el.dataset.reaction) > order.indexOf(emoji),
    )
    this.element.insertBefore(
      form,
      next ? next.closest("form") : this.addTarget,
    )
  }

  #render(pill, emoji, count, active) {
    pill.dataset.reaction = emoji
    pill.dataset.active = String(active)
    pill.textContent = `${emoji} ${count}`
  }

  #count(pill) {
    return parseInt(pill.textContent.trim().split(/\s+/).pop(), 10) || 0
  }

  #pickerOrder() {
    return Array.from(
      this.addTarget.querySelectorAll("input[name='emoji']"),
      (input) => input.value,
    )
  }

  #closePicker() {
    const popover = this.addTarget.querySelector(
      "[data-controller~='ui--popover']",
    )
    const controller =
      popover &&
      this.application.getControllerForElementAndIdentifier(
        popover,
        "ui--popover",
      )
    if (controller?.isOpen()) controller.hide()
  }
}
